import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useUserPermissions } from '../hooks/useUserPermissions'

type ProtectedRouteProps = {
  children: React.ReactNode
  requiredPermission?: string
}

function isTeamMemberUser(): boolean {
  try {
    const raw = localStorage.getItem('dizeeUser')
    const user = raw ? JSON.parse(raw) : null
    return !!user?.professionRole && ['manager', 'support'].includes(user.professionRole)
  } catch {
    return false
  }
}

export default function ProtectedRoute({ children, requiredPermission }: ProtectedRouteProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { hasPermission, loading } = useUserPermissions()

  useEffect(() => {
    if (loading) return

    // If no specific permission required, allow access
    if (!requiredPermission) return

    // Check if user has permission
    if (!hasPermission(requiredPermission)) {
      console.warn(`Access denied to ${location.pathname}: missing permission '${requiredPermission}'`)
      // Send team members to team-hub (not / which would redirect back here causing a loop)
      const dest = isTeamMemberUser() ? '/team-hub' : '/'
      navigate(dest, { replace: true })
    }
  }, [loading, hasPermission, requiredPermission, navigate, location.pathname])

  if (loading) {
    // Render an empty shell instead of "Loading..." text — the page structure
    // fills in once permissions resolve. Never show a loading message.
    return <div className="p-4 pt-16 md:pt-8 md:p-8 max-w-6xl mx-auto" />
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return null
  }

  return <>{children}</>
}
